import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/PersonOutlineRounded'
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { colors } from '../../const/colors'
import { IUserDataRow } from './HIDUsersTable'
import { IHIDConfig } from '../../graphql/hid/getHIDConfig'
import HIDAccount from './HIDAccount'

const CARD_SX = {
  background: colors.surface,
  border: `1px solid #EEF2F5`,
  borderRadius: '22px',
  boxShadow: '0 12px 30px -22px rgba(19,54,73,.25)',
  p: '22px',
} as const

const LABEL_SX = {
  fontSize: '11px',
  color: '#7E99AC',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  mb: '4px',
}

const Field: FC<{ label: string; value?: string; fullWidth?: boolean }> = ({
  label,
  value,
  fullWidth,
}) => (
  <Box sx={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography
      sx={{
        fontSize: '14.5px',
        fontWeight: 700,
        color: '#133649',
        wordBreak: fullWidth ? 'break-all' : undefined,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
)

const HIDUserDetails: FC<{
  userData: IUserDataRow
  closeDrawer: () => void
  listRefetch: () => void
  hidConfig?: IHIDConfig
}> = ({ userData, closeDrawer, listRefetch }) => {
  const { t } = useTranslation(['user_details', 'form', 'hid'])
  const { fullName, login, workEmail, firstName, lastName } = userData

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#F4F7F9',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '18px 24px',
          background: '#fff',
          borderBottom: '1px solid #E7EDF1',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '11px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#7E99AC',
              fontWeight: 700,
            }}
          >
            Mobilna karta
          </Typography>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              mt: '2px',
              color: '#133649',
            }}
          >
            {t('title')}
          </Typography>
        </Box>
        <Box
          onClick={closeDrawer}
          data-e2e="UserDetails-button-close"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: '#EFF3F6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#51626d',
            flexShrink: 0,
            '&:hover': { background: '#E1E9EE' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: '20px 24px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#1d506b,#133649)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '19px',
                  fontWeight: 800,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.15,
                }}
              >
                {fullName}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: '18px',
              pt: '18px',
              borderTop: '1px solid #EFF3F6',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px 18px',
            }}
          >
            <Field
              label={t('form:full_name')}
              value={[firstName, lastName].filter(Boolean).join(' ')}
            />
            <Field label={t('form:login')} value={login} />
            <Field label={t('form:work_email')} value={workEmail} fullWidth />
          </Box>
        </Box>

        <Box sx={CARD_SX}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              mb: '18px',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                background: 'rgba(19,54,73,0.07)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#133649',
                flexShrink: 0,
              }}
            >
              <SmartphoneOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                flex: 1,
              }}
            >
              {t('hid:mobile_access_card')}
            </Typography>
          </Box>
          <HIDAccount
            key={userData.login}
            userData={userData}
            listRefetch={listRefetch}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default HIDUserDetails
